import { useState } from "react";
import { Check, Sparkles, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Plan {
  name: string;
  tier: string;
  price: number;
  description: string;
  features: string[];
  gibGrant: number;
  popular?: boolean;
}

const plans: Plan[] = [
  {
    name: "Creator",
    tier: "creator",
    price: 20,
    description: "For knowledge creators and contributors",
    gibGrant: 1000,
    features: [
      "1,000 Owned Gibs monthly",
      "Create unlimited Knowledge Bases",
      "Participate in Sprints",
      "Basic analytics",
      "Community support",
    ],
  },
  {
    name: "Composer",
    tier: "composer",
    price: 50,
    description: "For solution builders and team leads",
    gibGrant: 2500,
    popular: true,
    features: [
      "2,500 Owned Gibs monthly",
      "Everything in Creator",
      "Create Solutions with bounties",
      "33% of Solution Bounty earnings",
      "Advanced analytics",
      "Priority support",
      "Team collaboration tools",
    ],
  },
];

export function SubscriptionPlans() {
  const [loading, setLoading] = useState<string | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubscribe = async (tier: string) => {
    if (!user) {
      toast({
        title: "Authentication required",
        description: "Please log in to subscribe",
        variant: "destructive",
      });
      return;
    }

    setLoading(tier);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout-session", {
        body: {
          type: "subscription",
          tier,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start checkout",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2 max-w-4xl mx-auto">
      {plans.map((plan) => (
        <Card
          key={plan.tier}
          className={`relative flex flex-col ${
            plan.popular
              ? "border-primary shadow-lg shadow-primary/20"
              : "border-border"
          }`}
        >
          {plan.popular && (
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">
              Most Popular
            </Badge>
          )}
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-2">
              {plan.tier === "composer" ? (
                <Crown className="h-10 w-10 text-primary" />
              ) : (
                <Sparkles className="h-10 w-10 text-primary" />
              )}
            </div>
            <CardTitle className="text-2xl">{plan.name}</CardTitle>
            <CardDescription>{plan.description}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <div className="text-center mb-6">
              <span className="text-4xl font-bold">${plan.price}</span>
              <span className="text-muted-foreground">/month</span>
              <p className="text-sm text-muted-foreground mt-1">
                +{plan.gibGrant.toLocaleString()} Owned Gibs
              </p>
            </div>
            <ul className="space-y-3">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter>
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={() => handleSubscribe(plan.tier)}
              disabled={loading !== null}
            >
              {loading === plan.tier ? "Loading..." : `Subscribe to ${plan.name}`}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}
